import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { preferencesService } from '../../services/preferences.service';
import { useAuth } from '../../contexts/AuthContext';
import { backendProvider } from '../../services/backend';
import {
  normalizeBackgroundEffect,
  parseAuroraColors,
  type AuroraColorStops,
  type BackgroundEffect,
} from '../../constants/backgroundEffects';
import TeamPreferencesTab from './TeamPreferencesTab';
import AccountSettingsTab from './AccountSettingsTab';
import BrandingSettingsTab from './BrandingSettingsTab';
import AuroraColorPicker from './AuroraColorPicker';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Deep-linkable initial tab (from #settings/<tab>). */
  initialTab?: SettingsTab;
}

type SettingsTab = 'appearance' | 'branding' | 'team' | 'account' | 'modules';

const BACKGROUND_OPTIONS: { id: BackgroundEffect; label: string; description: string }[] = [
  {
    id: 'none',
    label: 'Minimalist (Default)',
    description: 'Static dark background for performance'
  },
  {
    id: 'aurora',
    label: 'Aurora',
    description: 'Flowing light band with colours you can customize'
  },
  {
    id: 'lightrays',
    label: 'Cyber Rays',
    description: 'Dynamic light rays with mouse interaction'
  },
  {
    id: 'vanta-waves',
    label: 'Quantum Waves',
    description: 'Fluid 3D mesh waves simulation'
  }
];

const SettingsIcon = ({ name, className = 'h-5 w-5' }: { name: string; className?: string }) => {
  switch (name) {
    case 'settings':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'appearance':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M12 3c4.97 0 9 4.03 9 9 0 3.314-2.686 6-6 6h-1.2a1.8 1.8 0 01-1.8-1.8c0-.542.24-1.056.655-1.397l1.016-.836A1.8 1.8 0 0012.52 12H9.6A3.6 3.6 0 016 8.4 5.4 5.4 0 0111.4 3H12z" />
          <circle cx="7.5" cy="10.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="10.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
          <circle cx="15.5" cy="8.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'account':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM5 20a7 7 0 0114 0" />
        </svg>
      );
    case 'branding':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="3.5" y="4.5" width="17" height="15" rx="3" strokeWidth="1.9" />
          <circle cx="9" cy="10" r="2" strokeWidth="1.7" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="m5.5 17 4.2-4.2 2.8 2.8 2.2-2.2 3.8 3.6" />
        </svg>
      );
    case 'modules':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="4" y="4" width="7" height="7" rx="2" strokeWidth="1.9" />
          <rect x="13" y="4" width="7" height="7" rx="2" strokeWidth="1.9" />
          <rect x="4" y="13" width="7" height="7" rx="2" strokeWidth="1.9" />
          <rect x="13" y="13" width="7" height="7" rx="2" strokeWidth="1.9" />
        </svg>
      );
    case 'team':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="8.5" r="3" strokeWidth="1.9" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M3.5 19a5.5 5.5 0 0111 0M15.5 6.2a3 3 0 010 5.6M17.5 13.6a5.5 5.5 0 013 4.9" />
        </svg>
      );
    case 'lightrays':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M12 2v5m0 10v5M4.93 4.93l3.54 3.54m7.06 7.06 3.54 3.54M2 12h5m10 0h5M4.93 19.07l3.54-3.54m7.06-7.06 3.54-3.54" />
        </svg>
      );
    case 'aurora':
      return <Icon icon="solar:cloud-sun-bold" className={className} aria-hidden="true" />;
    case 'vanta-waves':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M2 14c2.2 0 2.2-4 4.4-4s2.2 4 4.4 4 2.2-4 4.4-4 2.2 4 4.4 4 2.2-4 4.4-4" />
        </svg>
      );
    case 'none':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <rect x="5" y="5" width="14" height="14" rx="2" strokeWidth="1.9" />
        </svg>
      );
    case 'info':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    default:
      return null;
  }
};

export default function SettingsModal({ isOpen, onClose, initialTab }: SettingsModalProps) {
  "use memo";
  const { data: userPreferences, refetch } = useUserPreferences();
  const { isTeamAdmin, isSuperAdmin, currentUser } = useAuth();

  // Team-manage access: the `teamadmin` LABEL (set by N8N provisioning) OR
  // team OWNER/admin membership roles. A freshly registered user creates the
  // team, so Appwrite lists them as membership role "owner" — without this
  // check they couldn't reach their own team settings until N8N sets the label.
  const { data: membershipRoles = [] } = useQuery({
    queryKey: ['team', 'membership-roles', currentUser?.uid || ''],
    queryFn: () => backendProvider.auth.getTeamMembershipRoles(),
    enabled: Boolean(currentUser?.uid),
    staleTime: 5 * 60 * 1000,
  });
  const isTeamOwner = membershipRoles.some(
    (role) => role === 'owner' || role === 'admin' || role === 'teamadmin',
  );
  // Per requirements: the TEAM section requires the `teamadmin` Appwrite user
  // label (set by N8N provisioning). Owner-membership alone no longer grants
  // it; superadmins keep access for support work.
  const canManageTeam = isTeamAdmin || isSuperAdmin;
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab ?? 'account');
  const [selectedEffect, setSelectedEffect] = useState<BackgroundEffect>('none');
  const [opacity, setOpacity] = useState<number>(0.7);
  const [auroraColors, setAuroraColors] = useState<AuroraColorStops>(parseAuroraColors(undefined));
  const [loading, setLoading] = useState(false);

  // Sync with current preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      const stored = userPreferences?.background_effect;
      // Legacy removed effects (vanta-dots / vanta-fog) show as Minimalist.
      setSelectedEffect(normalizeBackgroundEffect(stored));
      if (userPreferences?.background_opacity !== undefined) {
        setOpacity(userPreferences.background_opacity);
      }
      setAuroraColors(parseAuroraColors(userPreferences?.background_aurora_colors));
    }
  }, [isOpen, userPreferences]);

  // Follow deep-link tab changes while open (e.g. navigating to
  // #settings/team while the modal is already showing).
  useEffect(() => {
    if (isOpen && initialTab) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  // Keep the URL hash in sync with manual tab clicks (#settings/<tab>).
  const handleTabClick = (tab: SettingsTab) => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#settings/${tab}`);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await preferencesService.setBackgroundEffect(selectedEffect);
      await preferencesService.setBackgroundOpacity(opacity);
      // Persisted regardless of the chosen effect so custom colours survive
      // switching effects and coming back to Aurora later.
      await preferencesService.setAuroraColors(auroraColors);
      await refetch(); // Refresh preferences data
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-md">
      <button
        type="button"
        aria-label="Close settings"
        className="absolute inset-0"
        onClick={onClose}
      />
      <div className="relative flex h-[92vh] max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[34px] border border-white/10 bg-[linear-gradient(160deg,rgba(16,16,18,0.98),rgba(8,8,10,0.96))] shadow-[0_34px_100px_rgba(0,0,0,0.5)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(82,96,255,0.16),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(34,211,238,0.08),transparent_20%)]" />
        {/* Header — compact so the content panel gets the height */}
        <div className="relative flex items-center justify-between border-b border-white/8 px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/18 bg-cyan-400/8 px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/90">
              <SettingsIcon name="settings" className="h-3.5 w-3.5" />
              Workspace Settings
            </div>
            <p className="hidden text-sm leading-6 text-gray-400 md:block">
              Manage your workspace and the shared identity your team uses across Brainiacs.
            </p>
          </div>
          <button
            onClick={onClose}
            className="group flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all hover:border-white/20 hover:bg-white/10 hover:text-white"
          >
            <svg className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="relative flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-full max-w-[250px] shrink-0 border-r border-white/6 bg-black/20 p-3.5 md:p-4">
            <div className="space-y-2.5">
              {/* Account first — the primary section. */}
              <button
                onClick={() => handleTabClick('account')}
                className={`w-full rounded-2xl border px-3 py-2.5 text-left text-sm transition-all ${
                  activeTab === 'account'
                    ? 'border-[#5260FF]/32 bg-[linear-gradient(135deg,rgba(82,96,255,0.2),rgba(12,17,26,0.92))] text-white shadow-[0_16px_34px_rgba(82,96,255,0.14)]'
                    : 'border-white/8 bg-white/[0.03] text-gray-400 hover:border-white/16 hover:bg-white/[0.05] hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                    activeTab === 'account'
                      ? 'border-cyan-300/18 bg-cyan-400/10 text-cyan-100'
                      : 'border-white/10 bg-white/[0.03] text-gray-500'
                  }`}>
                    <SettingsIcon name="account" className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold">Account</span>
                    <span className="mt-0.5 block text-xs text-gray-500">User and company profile</span>
                  </span>
                </div>
              </button>

              <button
                onClick={() => handleTabClick('appearance')}
                className={`w-full rounded-2xl border px-3 py-2.5 text-left text-sm transition-all ${
                  activeTab === 'appearance'
                    ? 'border-[#5260FF]/32 bg-[linear-gradient(135deg,rgba(82,96,255,0.2),rgba(12,17,26,0.92))] text-white shadow-[0_16px_34px_rgba(82,96,255,0.14)]'
                    : 'border-white/8 bg-white/[0.03] text-gray-400 hover:border-white/16 hover:bg-white/[0.05] hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                    activeTab === 'appearance'
                      ? 'border-cyan-300/18 bg-cyan-400/10 text-cyan-100'
                      : 'border-white/10 bg-white/[0.03] text-gray-500'
                  }`}>
                    <SettingsIcon name="appearance" className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold">Appearance</span>
                    <span className="mt-0.5 block text-xs text-gray-500">Background and visual feel</span>
                  </span>
                </div>
              </button>
            </div>

            <div className="mt-4 space-y-2.5">
              <button
                onClick={() => handleTabClick('branding')}
                className={`w-full rounded-2xl border px-3 py-2.5 text-left text-sm transition-all ${
                  activeTab === 'branding'
                    ? 'border-[#5260FF]/32 bg-[linear-gradient(135deg,rgba(82,96,255,0.2),rgba(12,17,26,0.92))] text-white shadow-[0_16px_34px_rgba(82,96,255,0.14)]'
                    : 'border-white/8 bg-white/[0.03] text-gray-400 hover:border-white/16 hover:bg-white/[0.05] hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                    activeTab === 'branding'
                      ? 'border-cyan-300/18 bg-cyan-400/10 text-cyan-100'
                      : 'border-white/10 bg-white/[0.03] text-gray-500'
                  }`}>
                    <SettingsIcon name="branding" className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold">Branding</span>
                    <span className="mt-0.5 block text-xs text-gray-500">Logo and primary colour</span>
                  </span>
                </div>
              </button>

              {/* Modules & Products — its own section. */}
              <button
                onClick={() => handleTabClick('modules')}
                className={`w-full rounded-2xl border px-3 py-2.5 text-left text-sm transition-all ${
                  activeTab === 'modules'
                    ? 'border-[#5260FF]/32 bg-[linear-gradient(135deg,rgba(82,96,255,0.2),rgba(12,17,26,0.92))] text-white shadow-[0_16px_34px_rgba(82,96,255,0.14)]'
                    : 'border-white/8 bg-white/[0.03] text-gray-400 hover:border-white/16 hover:bg-white/[0.05] hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                    activeTab === 'modules'
                      ? 'border-cyan-300/18 bg-cyan-400/10 text-cyan-100'
                      : 'border-white/10 bg-white/[0.03] text-gray-500'
                  }`}>
                    <SettingsIcon name="modules" className="h-4 w-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold">Modules &amp; Products</span>
                    <span className="mt-0.5 block text-xs text-gray-500">Products, features and activation</span>
                  </span>
                </div>
              </button>

              {/* Team — teamadmin-labelled users only (see canManageTeam). */}
              {canManageTeam && (
                <button
                  onClick={() => handleTabClick('team')}
                  className={`w-full rounded-2xl border px-3 py-2.5 text-left text-sm transition-all ${
                    activeTab === 'team'
                      ? 'border-[#5260FF]/32 bg-[linear-gradient(135deg,rgba(82,96,255,0.2),rgba(12,17,26,0.92))] text-white shadow-[0_16px_34px_rgba(82,96,255,0.14)]'
                      : 'border-white/8 bg-white/[0.03] text-gray-400 hover:border-white/16 hover:bg-white/[0.05] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${
                      activeTab === 'team'
                        ? 'border-cyan-300/18 bg-cyan-400/10 text-cyan-100'
                        : 'border-white/10 bg-white/[0.03] text-gray-500'
                    }`}>
                      <SettingsIcon name="team" className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-semibold">Team</span>
                      <span className="mt-0.5 block text-xs text-gray-500">Team-wide defaults</span>
                    </span>
                  </div>
                </button>
              )}

            </div>
          </div>

          {/* Main Panel */}
          <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">
            {activeTab === 'appearance' && (
              <div className="space-y-7">
                <div className="rounded-[24px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 md:p-5">
                  <div className="mb-3.5">
                    <h3 className="text-base font-black tracking-tight text-white">Background Effect</h3>
                    <p className="mt-0.5 text-[13px] leading-5 text-gray-400">Choose the visual mode that frames your workspace.</p>
                  </div>

                  {/* Flat radio rows — hairline separated, no boxed cards. */}
                  <div className="divide-y divide-white/[0.06]">
                    {BACKGROUND_OPTIONS.map((option) => (
                      <button
                        type="button"
                        key={option.id}
                        onClick={() => setSelectedEffect(option.id)}
                        aria-pressed={selectedEffect === option.id}
                        className={`flex w-full items-center gap-3.5 py-2.5 text-left transition-colors ${
                          selectedEffect === option.id ? 'text-white' : 'text-white/70 hover:text-white'
                        }`}
                      >
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                          selectedEffect === option.id
                            ? 'border-cyan-300/24 bg-cyan-400/10 text-cyan-100'
                            : 'border-white/8 bg-white/[0.03] text-white/40'
                        }`}>
                          <SettingsIcon name={option.id} className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-bold">{option.label}</span>
                          <span className="mt-0.5 block text-xs leading-5 text-white/35">{option.description}</span>
                        </span>
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all ${
                          selectedEffect === option.id
                            ? 'border-[#5260FF] bg-[#5260FF] text-white'
                            : 'border-white/15 text-transparent'
                        }`}>
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.6" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedEffect === 'aurora' && (
                  <div className="rounded-[24px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 md:p-5">
                    <div className="mb-3.5">
                      <h3 className="text-base font-black tracking-tight text-white">Aurora Colours</h3>
                      <p className="mt-0.5 text-[13px] leading-5 text-gray-400">
                        Pick the three colours the aurora band blends across, or start from a preset.
                      </p>
                    </div>
                    <AuroraColorPicker colors={auroraColors} onChange={setAuroraColors} disabled={loading} />
                  </div>
                )}

                <div className="rounded-[24px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 md:p-5">
                  <div className="mb-3.5">
                    <h3 className="text-base font-black tracking-tight text-white">Background Opacity</h3>
                    <p className="mt-0.5 text-[13px] leading-5 text-gray-400">Control how visible the ambient motion feels behind your work.</p>
                  </div>

                  <div className="pt-1">
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        aria-label="Background opacity"
                        aria-valuetext={`${Math.round(opacity * 100)}%`}
                        min="0"
                        max="1"
                        step="0.05"
                        value={opacity}
                        onChange={(e) => setOpacity(parseFloat(e.target.value))}
                        className="flex-1 h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                          [&::-webkit-slider-thumb]:appearance-none
                          [&::-webkit-slider-thumb]:w-5
                          [&::-webkit-slider-thumb]:h-5
                          [&::-webkit-slider-thumb]:rounded-full
                          [&::-webkit-slider-thumb]:bg-[#5260FF]
                          [&::-webkit-slider-thumb]:shadow-lg
                          [&::-webkit-slider-thumb]:shadow-[#5260FF]/30
                          [&::-webkit-slider-thumb]:cursor-pointer
                          [&::-webkit-slider-thumb]:transition-all
                          [&::-webkit-slider-thumb]:hover:scale-110
                          [&::-moz-range-thumb]:w-5
                          [&::-moz-range-thumb]:h-5
                          [&::-moz-range-thumb]:rounded-full
                          [&::-moz-range-thumb]:bg-[#5260FF]
                          [&::-moz-range-thumb]:border-0
                          [&::-moz-range-thumb]:cursor-pointer"
                      />
                      <span className="min-w-[74px] rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-center text-sm font-mono text-white">
                        {Math.round(opacity * 100)}%
                      </span>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {[
                        { value: 0.3, label: 'Subtle' },
                        { value: 0.5, label: 'Medium' },
                        { value: 0.7, label: 'Default' },
                        { value: 1.0, label: 'Full' }
                      ].map((preset) => (
                        <button
                          key={preset.value}
                          onClick={() => setOpacity(preset.value)}
                          className={`h-8 rounded-lg border px-3 text-xs font-semibold transition-all ${
                            Math.abs(opacity - preset.value) < 0.05
                              ? 'border-[#5260FF]/30 bg-[#5260FF]/16 text-white'
                              : 'border-white/8 bg-white/[0.02] text-gray-400 hover:border-white/16 hover:text-white'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2 border-t border-white/[0.06] pt-3 text-amber-100/70">
                  <SettingsIcon name="info" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <p className="text-[11px] leading-5 text-amber-100/60">
                    Aurora and Quantum Waves create a richer atmosphere. Cyber Rays or Minimalist use fewer graphics resources on laptops.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <AccountSettingsTab />
            )}

            {activeTab === 'modules' && (
              <TeamPreferencesTab section="modules" onClose={onClose} />
            )}

            {activeTab === 'team' && canManageTeam && (
              <TeamPreferencesTab section="team" onClose={onClose} />
            )}

            {activeTab === 'branding' && (
              <BrandingSettingsTab />
            )}
          </div>
        </div>

        {/* Appearance has modal-level actions; Branding and Team save inline. */}
        {activeTab === 'appearance' && (
          <div className="relative flex justify-end gap-3 border-t border-white/8 bg-black/20 px-6 py-5 md:px-8">
            <button
              onClick={onClose}
              className="rounded-2xl border border-white/8 bg-white/[0.03] px-5 py-2.5 text-sm font-semibold text-gray-400 transition-colors hover:border-white/14 hover:bg-white/[0.05] hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-2xl border border-[#5260FF]/30 bg-[#5260FF] px-5 py-2.5 text-sm font-black text-white shadow-[0_16px_32px_rgba(82,96,255,0.24)] transition-all hover:bg-[#4553f1] disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
